// Cash Flow and FCF Data
// UPDATED: Now using REAL DATA from ABC Excel files
// Source: ABC_Liquidez_Geracao_Caixa.xlsx, ABC_Demonstracao_Resultados.xlsx

// Import real financial data
import { 
  realIncomeStatementData, 
  realCashFlowData, 
  realBalanceSheetData,
  toMillionsUSD 
} from './realFinancialData';

// ============================================================================
// FREE CASH FLOW DETAILS - Derived from real data where available
// ============================================================================
export const freeCashFlowDetailsData = {
  // 2024 ACTUAL - FROM REAL DATA
  2024: {
    ebitda: toMillionsUSD(realIncomeStatementData[2024].ebitda),  // 30.63 M USD
    workingCapitalChange: -8,  // Still mock - not in source files
    taxesPaid: toMillionsUSD(realCashFlowData[2024].pagamentosEstado),  // -7.72 M USD
    operatingCashFlow: toMillionsUSD(realCashFlowData[2024].fluxoCaixaOperacional),  // 13.97 M USD
    capex: toMillionsUSD(realCashFlowData[2024].fornecedoresAtivosFixos),  // -7.61 M USD
    freeCashFlow: toMillionsUSD(realCashFlowData[2024].fluxoCaixaOperacional + realCashFlowData[2024].fluxoCaixaInvestimento),  // 6.37 M USD
    dividends: toMillionsUSD(realCashFlowData[2024].dividendosPagos),  // 0
    debtRepayment: toMillionsUSD(realCashFlowData[2024].reembolsoFinanciamentos),  // -2.84 M USD
    netCashChange: toMillionsUSD(realCashFlowData[2024].variacaoCaixa),  // 8.22 M USD
    ratios: {
      fcfYield: 8.6,  // Calculated: FCF / Market Cap (mock market cap)
      fcfToRevenue: (realCashFlowData[2024].fluxoCaixaOperacional + realCashFlowData[2024].fluxoCaixaInvestimento) / realIncomeStatementData[2024].totalProveitos * 100,  // 6.99%
      fcfConversion: (realCashFlowData[2024].fluxoCaixaOperacional + realCashFlowData[2024].fluxoCaixaInvestimento) / realIncomeStatementData[2024].ebitda * 100,  // 20.8%
      cashConversionCycle: 45,  // Mock - not in source
    },
  },
  // 2025 BUDGET - FROM REAL DATA
  2025: {
    ebitda: toMillionsUSD(realIncomeStatementData[2025].ebitda),  // 32.88 M USD
    workingCapitalChange: -5,  // Mock
    taxesPaid: toMillionsUSD(realCashFlowData[2025].pagamentosEstado),  // -7.70 M USD
    operatingCashFlow: toMillionsUSD(realCashFlowData[2025].fluxoCaixaOperacional),  // 33.86 M USD
    capex: toMillionsUSD(realCashFlowData[2025].fornecedoresAtivosFixos),  // -32.90 M USD
    freeCashFlow: toMillionsUSD(realCashFlowData[2025].fluxoCaixaOperacional + realCashFlowData[2025].fluxoCaixaInvestimento),  // 0.96 M USD
    dividends: toMillionsUSD(realCashFlowData[2025].dividendosPagos),  // -9.0 M USD
    debtRepayment: toMillionsUSD(realCashFlowData[2025].reembolsoFinanciamentos),  // -7.77 M USD
    netCashChange: toMillionsUSD(realCashFlowData[2025].variacaoCaixa),  // -4.03 M USD
    ratios: {
      fcfYield: 3.2,
      fcfToRevenue: (realCashFlowData[2025].fluxoCaixaOperacional + realCashFlowData[2025].fluxoCaixaInvestimento) / realIncomeStatementData[2025].totalProveitos * 100,  // 0.91%
      fcfConversion: (realCashFlowData[2025].fluxoCaixaOperacional + realCashFlowData[2025].fluxoCaixaInvestimento) / realIncomeStatementData[2025].ebitda * 100,  // 2.91%
      cashConversionCycle: 38,
    },
  },
  // 2026 BUDGET - FROM REAL DATA
  2026: {
    ebitda: toMillionsUSD(realIncomeStatementData[2026].annual.ebitda),  // 51.55 M USD
    workingCapitalChange: -3,  // Mock
    taxesPaid: toMillionsUSD(realCashFlowData[2026].annual.pagamentosEstado),  // -6.70 M USD
    operatingCashFlow: toMillionsUSD(realCashFlowData[2026].annual.fluxoCaixaOperacional),  // 45.21 M USD
    capex: toMillionsUSD(realCashFlowData[2026].annual.fornecedoresAtivosFixos),  // -43.05 M USD
    freeCashFlow: toMillionsUSD(realCashFlowData[2026].annual.fluxoCaixaOperacional + realCashFlowData[2026].annual.fluxoCaixaInvestimento),  // 2.16 M USD
    dividends: toMillionsUSD(realCashFlowData[2026].annual.dividendosPagos),  // -6.0 M USD
    debtRepayment: toMillionsUSD(realCashFlowData[2026].annual.reembolsoFinanciamentos),  // -15.48 M USD
    netCashChange: toMillionsUSD(realCashFlowData[2026].annual.variacaoCaixa),  // -2.20 M USD
    ratios: {
      fcfYield: 4.2,
      fcfToRevenue: (realCashFlowData[2026].annual.fluxoCaixaOperacional + realCashFlowData[2026].annual.fluxoCaixaInvestimento) / realIncomeStatementData[2026].annual.totalProveitos * 100,  // 1.60%
      fcfConversion: (realCashFlowData[2026].annual.fluxoCaixaOperacional + realCashFlowData[2026].annual.fluxoCaixaInvestimento) / realIncomeStatementData[2026].annual.ebitda * 100,  // 4.18%
      cashConversionCycle: 42,
    },
  },
};

// ============================================================================
// DETAILED CASH FLOW STATEMENT - FROM REAL DATA
// ============================================================================
export const cashFlowStatementData = {
  // 2024 ACTUAL
  2024: {
    openingCash: toMillionsUSD(realCashFlowData[2024].caixaInicioPeriodo),  // 7.68 M USD
    customerReceipts: toMillionsUSD(realCashFlowData[2024].recebimentosClientes),  // 74.76 M USD
    supplierPayments: toMillionsUSD(realCashFlowData[2024].pagamentosFornecedores),  // -35.45 M USD
    personnelPayments: toMillionsUSD(realCashFlowData[2024].pagamentosPessoal),  // -19.18 M USD
    cashFromOperations: toMillionsUSD(realCashFlowData[2024].caixaGeradaOperacoes),  // 20.12 M USD
    taxPayments: toMillionsUSD(realCashFlowData[2024].pagamentosEstado),  // -7.72 M USD
    otherOperating: toMillionsUSD(realCashFlowData[2024].outrosRecebimentosPagamentos),  // 1.57 M USD
    operatingCashFlow: toMillionsUSD(realCashFlowData[2024].fluxoCaixaOperacional),  // 13.97 M USD
    fixedAssetPayments: toMillionsUSD(realCashFlowData[2024].fornecedoresAtivosFixos),  // -7.61 M USD
    financialInvestments: toMillionsUSD(realCashFlowData[2024].investimentosFinanceiros),  // 0
    investingCashFlow: toMillionsUSD(realCashFlowData[2024].fluxoCaixaInvestimento),  // -7.61 M USD
    loansReceived: toMillionsUSD(realCashFlowData[2024].financiamentosObtidos),  // 5.98 M USD
    interestReceived: toMillionsUSD(realCashFlowData[2024].jurosRendimentosSimilares),  // 0
    loanRepayments: toMillionsUSD(realCashFlowData[2024].reembolsoFinanciamentos),  // -2.84 M USD
    interestPaid: toMillionsUSD(realCashFlowData[2024].jurosGastosSimilares),  // -1.28 M USD
    dividendsPaid: toMillionsUSD(realCashFlowData[2024].dividendosPagos),  // 0
    financingCashFlow: toMillionsUSD(realCashFlowData[2024].fluxoCaixaFinanciamento),  // 1.85 M USD
    netCashChange: toMillionsUSD(realCashFlowData[2024].variacaoCaixa),  // 8.22 M USD
    fxEffect: toMillionsUSD(realCashFlowData[2024].efeitoDiferencasCambio),  // 0.55 M USD
    closingCash: toMillionsUSD(realCashFlowData[2024].caixaFinalPeriodo),  // 16.45 M USD
  },
  // 2025 BUDGET
  2025: {
    openingCash: toMillionsUSD(realCashFlowData[2025].caixaInicioPeriodo),  // 16.45 M USD
    customerReceipts: toMillionsUSD(realCashFlowData[2025].recebimentosClientes),  // 107.90 M USD
    supplierPayments: toMillionsUSD(realCashFlowData[2025].pagamentosFornecedores),  // -42.95 M USD
    personnelPayments: toMillionsUSD(realCashFlowData[2025].pagamentosPessoal),  // -22.65 M USD
    cashFromOperations: toMillionsUSD(realCashFlowData[2025].caixaGeradaOperacoes),  // 42.30 M USD
    taxPayments: toMillionsUSD(realCashFlowData[2025].pagamentosEstado),  // -7.70 M USD
    otherOperating: toMillionsUSD(realCashFlowData[2025].outrosRecebimentosPagamentos),  // -0.74 M USD
    operatingCashFlow: toMillionsUSD(realCashFlowData[2025].fluxoCaixaOperacional),  // 33.86 M USD
    fixedAssetPayments: toMillionsUSD(realCashFlowData[2025].fornecedoresAtivosFixos),  // -32.90 M USD
    financialInvestments: toMillionsUSD(realCashFlowData[2025].investimentosFinanceiros),  // 0
    investingCashFlow: toMillionsUSD(realCashFlowData[2025].fluxoCaixaInvestimento),  // -32.90 M USD
    loansReceived: toMillionsUSD(realCashFlowData[2025].financiamentosObtidos),  // 12.85 M USD
    interestReceived: toMillionsUSD(realCashFlowData[2025].jurosRendimentosSimilares),  // 0.025 M USD
    loanRepayments: toMillionsUSD(realCashFlowData[2025].reembolsoFinanciamentos),  // -7.77 M USD
    interestPaid: toMillionsUSD(realCashFlowData[2025].jurosGastosSimilares),  // -1.09 M USD
    dividendsPaid: toMillionsUSD(realCashFlowData[2025].dividendosPagos),  // -9.0 M USD
    financingCashFlow: toMillionsUSD(realCashFlowData[2025].fluxoCaixaFinanciamento),  // -4.99 M USD
    netCashChange: toMillionsUSD(realCashFlowData[2025].variacaoCaixa),  // -4.03 M USD
    fxEffect: toMillionsUSD(realCashFlowData[2025].efeitoDiferencasCambio),  // 0.67 M USD
    closingCash: toMillionsUSD(realCashFlowData[2025].caixaFinalPeriodo),  // 13.09 M USD
  },
  // 2026 BUDGET
  2026: {
    openingCash: toMillionsUSD(realCashFlowData[2026].annual.caixaInicioPeriodo),  // 13.09 M USD
    customerReceipts: toMillionsUSD(realCashFlowData[2026].annual.recebimentosClientes),  // 133.23 M USD
    supplierPayments: toMillionsUSD(realCashFlowData[2026].annual.pagamentosFornecedores),  // -50.00 M USD
    personnelPayments: toMillionsUSD(realCashFlowData[2026].annual.pagamentosPessoal),  // -30.02 M USD
    cashFromOperations: toMillionsUSD(realCashFlowData[2026].annual.caixaGeradaOperacoes),  // 53.21 M USD
    taxPayments: toMillionsUSD(realCashFlowData[2026].annual.pagamentosEstado),  // -6.70 M USD
    otherOperating: toMillionsUSD(realCashFlowData[2026].annual.outrosRecebimentosPagamentos),  // -1.30 M USD
    operatingCashFlow: toMillionsUSD(realCashFlowData[2026].annual.fluxoCaixaOperacional),  // 45.21 M USD
    fixedAssetPayments: toMillionsUSD(realCashFlowData[2026].annual.fornecedoresAtivosFixos),  // -43.05 M USD
    financialInvestments: toMillionsUSD(realCashFlowData[2026].annual.investimentosFinanceiros),  // 0
    investingCashFlow: toMillionsUSD(realCashFlowData[2026].annual.fluxoCaixaInvestimento),  // -43.05 M USD
    loansReceived: toMillionsUSD(realCashFlowData[2026].annual.financiamentosObtidos),  // 20.93 M USD
    interestReceived: toMillionsUSD(realCashFlowData[2026].annual.jurosRendimentosSimilares),  // 0.12 M USD
    loanRepayments: toMillionsUSD(realCashFlowData[2026].annual.reembolsoFinanciamentos),  // -15.48 M USD
    interestPaid: toMillionsUSD(realCashFlowData[2026].annual.jurosGastosSimilares),  // -3.93 M USD
    dividendsPaid: toMillionsUSD(realCashFlowData[2026].annual.dividendosPagos),  // -6.0 M USD
    financingCashFlow: toMillionsUSD(realCashFlowData[2026].annual.fluxoCaixaFinanciamento),  // -4.36 M USD
    netCashChange: toMillionsUSD(realCashFlowData[2026].annual.variacaoCaixa),  // -2.20 M USD
    fxEffect: toMillionsUSD(realCashFlowData[2026].annual.efeitoDiferencasCambio),  // -0.20 M USD
    closingCash: toMillionsUSD(realCashFlowData[2026].annual.caixaFinalPeriodo),  // 10.69 M USD
  },
};

// ============================================================================
// INCOME STATEMENT - FROM REAL DATA
// NEW: Includes "Variação nos PA e PVF" line item
// ============================================================================
export const incomeStatementData = {
  // 2024 ACTUAL - FROM REAL DATA
  2024: {
    vendas: toMillionsUSD(realIncomeStatementData[2024].vendas),  // 93.79 M USD
    prestacoesServicos: toMillionsUSD(realIncomeStatementData[2024].prestacoesServicos),  // 0
    outrosProveitos: toMillionsUSD(realIncomeStatementData[2024].outrosProveitos),  // 0.21 M USD
    variacaoPAePVF: toMillionsUSD(realIncomeStatementData[2024].variacaoPAePVF),  // -2.90 M USD (NEW LINE ITEM)
    totalProveitos: toMillionsUSD(realIncomeStatementData[2024].totalProveitos),  // 91.10 M USD
    cmvmc: toMillionsUSD(realIncomeStatementData[2024].cmvmc),  // -12.02 M USD
    custosComercializacao: toMillionsUSD(realIncomeStatementData[2024].custosComercializacao),  // -60.47 M USD
    custoPessoal: toMillionsUSD(realIncomeStatementData[2024].custosPessoal),  // -32.99 M USD
    subcontratos: toMillionsUSD(realIncomeStatementData[2024].subcontratos),  // -6.38 M USD
    geraisAdministrativos: toMillionsUSD(realIncomeStatementData[2024].geraisAdministrativos),  // -6.73 M USD
    totalCustosOperacionais: toMillionsUSD(realIncomeStatementData[2024].totalCustosOperacionais),  // -30.63 M USD
    ebitda: toMillionsUSD(realIncomeStatementData[2024].ebitda),  // 30.63 M USD
    margemEbitda: parseFloat((realIncomeStatementData[2024].margemEbitda * 100).toFixed(2)),  // 33.62%
    amortizacoes: toMillionsUSD(realIncomeStatementData[2024].amortizacoes),  // -8.31 M USD
    resultadoOperacional: toMillionsUSD(realIncomeStatementData[2024].ebit),  // 22.32 M USD
    resultadosNaoOperacionais: toMillionsUSD(realIncomeStatementData[2024].resultadosNaoOperacionais),  // -6.01 M USD
    resultadosFinanceiros: toMillionsUSD(realIncomeStatementData[2024].resultadosFinanceiros),  // 1.06 M USD
    resultadosAntesImpostos: toMillionsUSD(realIncomeStatementData[2024].resultadoAntesImposto),  // 17.37 M USD
    impostos: toMillionsUSD(realIncomeStatementData[2024].impostoIndustrial),  // -4.04 M USD
    resultadoLiquido: toMillionsUSD(realIncomeStatementData[2024].resultadoLiquido),  // 13.33 M USD
  },
  // 2025 BUDGET - FROM REAL DATA
  2025: {
    vendas: toMillionsUSD(realIncomeStatementData[2025].vendas),  // 104.25 M USD
    prestacoesServicos: toMillionsUSD(realIncomeStatementData[2025].prestacoesServicos),  // 0
    outrosProveitos: toMillionsUSD(realIncomeStatementData[2025].outrosProveitos),  // 0.49 M USD
    variacaoPAePVF: toMillionsUSD(realIncomeStatementData[2025].variacaoPAePVF),  // 0.71 M USD (NEW LINE ITEM)
    totalProveitos: toMillionsUSD(realIncomeStatementData[2025].totalProveitos),  // 105.45 M USD
    cmvmc: toMillionsUSD(realIncomeStatementData[2025].cmvmc),  // -19.23 M USD
    custosComercializacao: toMillionsUSD(realIncomeStatementData[2025].custosComercializacao),  // -72.57 M USD
    custoPessoal: toMillionsUSD(realIncomeStatementData[2025].custosPessoal),  // -37.92 M USD
    subcontratos: toMillionsUSD(realIncomeStatementData[2025].subcontratos),  // -4.91 M USD
    geraisAdministrativos: toMillionsUSD(realIncomeStatementData[2025].geraisAdministrativos),  // -7.61 M USD
    totalCustosOperacionais: toMillionsUSD(realIncomeStatementData[2025].totalCustosOperacionais),  // -32.88 M USD
    ebitda: toMillionsUSD(realIncomeStatementData[2025].ebitda),  // 32.88 M USD
    margemEbitda: parseFloat((realIncomeStatementData[2025].margemEbitda * 100).toFixed(2)),  // 31.18%
    amortizacoes: toMillionsUSD(realIncomeStatementData[2025].amortizacoes),  // -10.68 M USD
    resultadoOperacional: toMillionsUSD(realIncomeStatementData[2025].ebit),  // 22.20 M USD
    resultadosNaoOperacionais: toMillionsUSD(realIncomeStatementData[2025].resultadosNaoOperacionais),  // -9.23 M USD
    resultadosFinanceiros: toMillionsUSD(realIncomeStatementData[2025].resultadosFinanceiros),  // -1.36 M USD
    resultadosAntesImpostos: toMillionsUSD(realIncomeStatementData[2025].resultadoAntesImposto),  // 11.61 M USD
    impostos: toMillionsUSD(realIncomeStatementData[2025].impostoIndustrial),  // -2.90 M USD
    resultadoLiquido: toMillionsUSD(realIncomeStatementData[2025].resultadoLiquido),  // 8.71 M USD
  },
  // 2026 BUDGET (Annual) - FROM REAL DATA
  2026: {
    vendas: toMillionsUSD(realIncomeStatementData[2026].annual.vendas),  // 131.92 M USD
    prestacoesServicos: toMillionsUSD(realIncomeStatementData[2026].annual.prestacoesServicos),  // 0
    outrosProveitos: toMillionsUSD(realIncomeStatementData[2026].annual.outrosProveitos),  // 1.31 M USD
    variacaoPAePVF: toMillionsUSD(realIncomeStatementData[2026].annual.variacaoPAePVF),  // 1.34 M USD (NEW LINE ITEM)
    totalProveitos: toMillionsUSD(realIncomeStatementData[2026].annual.totalProveitos),  // 134.57 M USD
    cmvmc: toMillionsUSD(realIncomeStatementData[2026].annual.cmvmc),  // -23.81 M USD
    custosComercializacao: toMillionsUSD(realIncomeStatementData[2026].annual.custosComercializacao),  // -83.02 M USD
    custoPessoal: toMillionsUSD(realIncomeStatementData[2026].annual.custosPessoal),  // -41.61 M USD
    subcontratos: toMillionsUSD(realIncomeStatementData[2026].annual.subcontratos),  // -7.19 M USD
    geraisAdministrativos: toMillionsUSD(realIncomeStatementData[2026].annual.geraisAdministrativos),  // -9.31 M USD
    totalCustosOperacionais: toMillionsUSD(realIncomeStatementData[2026].annual.totalCustosOperacionais),  // -51.55 M USD
    ebitda: toMillionsUSD(realIncomeStatementData[2026].annual.ebitda),  // 51.55 M USD
    margemEbitda: parseFloat((realIncomeStatementData[2026].annual.margemEbitda * 100).toFixed(2)),  // 38.31%
    amortizacoes: toMillionsUSD(realIncomeStatementData[2026].annual.amortizacoes),  // -18.93 M USD
    resultadoOperacional: toMillionsUSD(realIncomeStatementData[2026].annual.ebit),  // 32.63 M USD
    resultadosNaoOperacionais: toMillionsUSD(realIncomeStatementData[2026].annual.resultadosNaoOperacionais),  // -4.40 M USD
    resultadosFinanceiros: toMillionsUSD(realIncomeStatementData[2026].annual.resultadosFinanceiros),  // -5.01 M USD
    resultadosAntesImpostos: toMillionsUSD(realIncomeStatementData[2026].annual.resultadoAntesImposto),  // 23.21 M USD
    impostos: toMillionsUSD(realIncomeStatementData[2026].annual.impostoIndustrial),  // -5.80 M USD
    resultadoLiquido: toMillionsUSD(realIncomeStatementData[2026].annual.resultadoLiquido),  // 17.41 M USD
  },
};

// ============================================================================
// QUARTERLY INCOME STATEMENT DATA - 2026 Budget
// ============================================================================
export const incomeStatementQuarterlyData = {
  2026: {
    Q1: {
      vendas: toMillionsUSD(realIncomeStatementData[2026].Q1.vendas),
      outrosProveitos: toMillionsUSD(realIncomeStatementData[2026].Q1.outrosProveitos),
      variacaoPAePVF: toMillionsUSD(realIncomeStatementData[2026].Q1.variacaoPAePVF),
      totalProveitos: toMillionsUSD(realIncomeStatementData[2026].Q1.totalProveitos),
      cmvmc: toMillionsUSD(realIncomeStatementData[2026].Q1.cmvmc),
      custosPessoal: toMillionsUSD(realIncomeStatementData[2026].Q1.custosPessoal),
      subcontratos: toMillionsUSD(realIncomeStatementData[2026].Q1.subcontratos),
      geraisAdministrativos: toMillionsUSD(realIncomeStatementData[2026].Q1.geraisAdministrativos),
      custosComercializacao: toMillionsUSD(realIncomeStatementData[2026].Q1.custosComercializacao),
      ebitda: toMillionsUSD(realIncomeStatementData[2026].Q1.ebitda),
      margemEbitda: parseFloat((realIncomeStatementData[2026].Q1.margemEbitda * 100).toFixed(2)),
      amortizacoes: toMillionsUSD(realIncomeStatementData[2026].Q1.amortizacoes),
      ebit: toMillionsUSD(realIncomeStatementData[2026].Q1.ebit),
      resultadosNaoOperacionais: toMillionsUSD(realIncomeStatementData[2026].Q1.resultadosNaoOperacionais),
      resultadosFinanceiros: toMillionsUSD(realIncomeStatementData[2026].Q1.resultadosFinanceiros),
      resultadoAntesImposto: toMillionsUSD(realIncomeStatementData[2026].Q1.resultadoAntesImposto),
      impostoIndustrial: toMillionsUSD(realIncomeStatementData[2026].Q1.impostoIndustrial),
      resultadoLiquido: toMillionsUSD(realIncomeStatementData[2026].Q1.resultadoLiquido),
    },
    Q2: {
      vendas: toMillionsUSD(realIncomeStatementData[2026].Q2.vendas),
      outrosProveitos: toMillionsUSD(realIncomeStatementData[2026].Q2.outrosProveitos),
      variacaoPAePVF: toMillionsUSD(realIncomeStatementData[2026].Q2.variacaoPAePVF),
      totalProveitos: toMillionsUSD(realIncomeStatementData[2026].Q2.totalProveitos),
      cmvmc: toMillionsUSD(realIncomeStatementData[2026].Q2.cmvmc),
      custosPessoal: toMillionsUSD(realIncomeStatementData[2026].Q2.custosPessoal),
      subcontratos: toMillionsUSD(realIncomeStatementData[2026].Q2.subcontratos),
      geraisAdministrativos: toMillionsUSD(realIncomeStatementData[2026].Q2.geraisAdministrativos),
      custosComercializacao: toMillionsUSD(realIncomeStatementData[2026].Q2.custosComercializacao),
      ebitda: toMillionsUSD(realIncomeStatementData[2026].Q2.ebitda),
      margemEbitda: parseFloat((realIncomeStatementData[2026].Q2.margemEbitda * 100).toFixed(2)),
      amortizacoes: toMillionsUSD(realIncomeStatementData[2026].Q2.amortizacoes),
      ebit: toMillionsUSD(realIncomeStatementData[2026].Q2.ebit),
      resultadosNaoOperacionais: toMillionsUSD(realIncomeStatementData[2026].Q2.resultadosNaoOperacionais),
      resultadosFinanceiros: toMillionsUSD(realIncomeStatementData[2026].Q2.resultadosFinanceiros),
      resultadoAntesImposto: toMillionsUSD(realIncomeStatementData[2026].Q2.resultadoAntesImposto),
      impostoIndustrial: toMillionsUSD(realIncomeStatementData[2026].Q2.impostoIndustrial),
      resultadoLiquido: toMillionsUSD(realIncomeStatementData[2026].Q2.resultadoLiquido),
    },
    Q3: {
      vendas: toMillionsUSD(realIncomeStatementData[2026].Q3.vendas),
      outrosProveitos: toMillionsUSD(realIncomeStatementData[2026].Q3.outrosProveitos),
      variacaoPAePVF: toMillionsUSD(realIncomeStatementData[2026].Q3.variacaoPAePVF),
      totalProveitos: toMillionsUSD(realIncomeStatementData[2026].Q3.totalProveitos),
      cmvmc: toMillionsUSD(realIncomeStatementData[2026].Q3.cmvmc),
      custosPessoal: toMillionsUSD(realIncomeStatementData[2026].Q3.custosPessoal),
      subcontratos: toMillionsUSD(realIncomeStatementData[2026].Q3.subcontratos),
      geraisAdministrativos: toMillionsUSD(realIncomeStatementData[2026].Q3.geraisAdministrativos),
      custosComercializacao: toMillionsUSD(realIncomeStatementData[2026].Q3.custosComercializacao),
      ebitda: toMillionsUSD(realIncomeStatementData[2026].Q3.ebitda),
      margemEbitda: parseFloat((realIncomeStatementData[2026].Q3.margemEbitda * 100).toFixed(2)),
      amortizacoes: toMillionsUSD(realIncomeStatementData[2026].Q3.amortizacoes),
      ebit: toMillionsUSD(realIncomeStatementData[2026].Q3.ebit),
      resultadosNaoOperacionais: toMillionsUSD(realIncomeStatementData[2026].Q3.resultadosNaoOperacionais),
      resultadosFinanceiros: toMillionsUSD(realIncomeStatementData[2026].Q3.resultadosFinanceiros),
      resultadoAntesImposto: toMillionsUSD(realIncomeStatementData[2026].Q3.resultadoAntesImposto),
      impostoIndustrial: toMillionsUSD(realIncomeStatementData[2026].Q3.impostoIndustrial),
      resultadoLiquido: toMillionsUSD(realIncomeStatementData[2026].Q3.resultadoLiquido),
    },
    Q4: {
      vendas: toMillionsUSD(realIncomeStatementData[2026].Q4.vendas),
      outrosProveitos: toMillionsUSD(realIncomeStatementData[2026].Q4.outrosProveitos),
      variacaoPAePVF: toMillionsUSD(realIncomeStatementData[2026].Q4.variacaoPAePVF),
      totalProveitos: toMillionsUSD(realIncomeStatementData[2026].Q4.totalProveitos),
      cmvmc: toMillionsUSD(realIncomeStatementData[2026].Q4.cmvmc),
      custosPessoal: toMillionsUSD(realIncomeStatementData[2026].Q4.custosPessoal),
      subcontratos: toMillionsUSD(realIncomeStatementData[2026].Q4.subcontratos),
      geraisAdministrativos: toMillionsUSD(realIncomeStatementData[2026].Q4.geraisAdministrativos),
      custosComercializacao: toMillionsUSD(realIncomeStatementData[2026].Q4.custosComercializacao),
      ebitda: toMillionsUSD(realIncomeStatementData[2026].Q4.ebitda),
      margemEbitda: parseFloat((realIncomeStatementData[2026].Q4.margemEbitda * 100).toFixed(2)),
      amortizacoes: toMillionsUSD(realIncomeStatementData[2026].Q4.amortizacoes),
      ebit: toMillionsUSD(realIncomeStatementData[2026].Q4.ebit),
      resultadosNaoOperacionais: toMillionsUSD(realIncomeStatementData[2026].Q4.resultadosNaoOperacionais),
      resultadosFinanceiros: toMillionsUSD(realIncomeStatementData[2026].Q4.resultadosFinanceiros),
      resultadoAntesImposto: toMillionsUSD(realIncomeStatementData[2026].Q4.resultadoAntesImposto),
      impostoIndustrial: toMillionsUSD(realIncomeStatementData[2026].Q4.impostoIndustrial),
      resultadoLiquido: toMillionsUSD(realIncomeStatementData[2026].Q4.resultadoLiquido),
    },
  },
};

// ============================================================================
// NET INCOME DETAILS - FROM REAL DATA
// ============================================================================
export const netIncomeDetailsData = {
  // 2024 ACTUAL - FROM REAL DATA
  2024: {
    ebitda: toMillionsUSD(realIncomeStatementData[2024].ebitda),  // 30.63 M USD
    depreciation: toMillionsUSD(realIncomeStatementData[2024].amortizacoes),  // -8.31 M USD
    ebit: toMillionsUSD(realIncomeStatementData[2024].ebit),  // 22.32 M USD
    financialResult: toMillionsUSD(realIncomeStatementData[2024].resultadosFinanceiros),  // 1.06 M USD
    nonOperatingResult: toMillionsUSD(realIncomeStatementData[2024].resultadosNaoOperacionais),  // -6.01 M USD
    ebt: toMillionsUSD(realIncomeStatementData[2024].resultadoAntesImposto),  // 17.37 M USD
    taxes: toMillionsUSD(realIncomeStatementData[2024].impostoIndustrial),  // -4.04 M USD
    netIncome: toMillionsUSD(realIncomeStatementData[2024].resultadoLiquido),  // 13.33 M USD
    netMargin: (realIncomeStatementData[2024].resultadoLiquido / realIncomeStatementData[2024].totalProveitos) * 100,  // 14.63%
    eps: toMillionsUSD(realIncomeStatementData[2024].resultadoLiquido) / 15,  // Assuming 15M shares
  },
  // 2025 BUDGET - FROM REAL DATA
  2025: {
    ebitda: toMillionsUSD(realIncomeStatementData[2025].ebitda),  // 32.88 M USD
    depreciation: toMillionsUSD(realIncomeStatementData[2025].amortizacoes),  // -10.68 M USD
    ebit: toMillionsUSD(realIncomeStatementData[2025].ebit),  // 22.20 M USD
    financialResult: toMillionsUSD(realIncomeStatementData[2025].resultadosFinanceiros),  // -1.36 M USD
    nonOperatingResult: toMillionsUSD(realIncomeStatementData[2025].resultadosNaoOperacionais),  // -9.23 M USD
    ebt: toMillionsUSD(realIncomeStatementData[2025].resultadoAntesImposto),  // 11.61 M USD
    taxes: toMillionsUSD(realIncomeStatementData[2025].impostoIndustrial),  // -2.90 M USD
    netIncome: toMillionsUSD(realIncomeStatementData[2025].resultadoLiquido),  // 8.71 M USD
    netMargin: (realIncomeStatementData[2025].resultadoLiquido / realIncomeStatementData[2025].totalProveitos) * 100,  // 8.26%
    eps: toMillionsUSD(realIncomeStatementData[2025].resultadoLiquido) / 15,
  },
  // 2026 BUDGET - FROM REAL DATA
  2026: {
    ebitda: toMillionsUSD(realIncomeStatementData[2026].annual.ebitda),  // 51.55 M USD
    depreciation: toMillionsUSD(realIncomeStatementData[2026].annual.amortizacoes),  // -18.93 M USD
    ebit: toMillionsUSD(realIncomeStatementData[2026].annual.ebit),  // 32.63 M USD
    financialResult: toMillionsUSD(realIncomeStatementData[2026].annual.resultadosFinanceiros),  // -5.01 M USD
    nonOperatingResult: toMillionsUSD(realIncomeStatementData[2026].annual.resultadosNaoOperacionais),  // -4.40 M USD
    ebt: toMillionsUSD(realIncomeStatementData[2026].annual.resultadoAntesImposto),  // 23.21 M USD
    taxes: toMillionsUSD(realIncomeStatementData[2026].annual.impostoIndustrial),  // -5.80 M USD
    netIncome: toMillionsUSD(realIncomeStatementData[2026].annual.resultadoLiquido),  // 17.41 M USD
    netMargin: (realIncomeStatementData[2026].annual.resultadoLiquido / realIncomeStatementData[2026].annual.totalProveitos) * 100,  // 12.94%
    eps: toMillionsUSD(realIncomeStatementData[2026].annual.resultadoLiquido) / 15,
  },
};
